'use client'

import styles from './Publisher.module.scss'
import { useState, useEffect, useContext, useRef } from 'react'
import { RootContext } from '@/modules/contextService'
import { DateTime } from 'luxon'
import Link from 'next/link'
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Snackbar
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ListItemIcon from '@mui/material/ListItemIcon'
import FacebookIcon from '@mui/icons-material/Facebook'
import XIcon from '@mui/icons-material/X'
import SendIcon from '@mui/icons-material/Send'
import LinkIcon from '@mui/icons-material/Link'
import Divider from '@mui/material/Divider'
// import EditNoteIcon from '@mui/icons-material/EditNote'
import TranslateIcon from '@mui/icons-material/Translate'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import ThemeClient from '@/app/v1/ThemeClient'
import cookieService from '@/modules/cookieService'
// import clientGetLocale from '@/modules/langService/clientGetLocale'
import { useTranslations } from 'next-intl'
import DeleteConfirmation from '@/components/inputs/DeleteConfirmation'
import { PATHWAY } from '@/modules/httpService'
import { useRouter } from 'next/navigation'
import Chip from '@mui/material/Chip'
import copyLinkService from '@/modules/copyLinkService'

interface PublisherProps {
  username: string | null
  publishDate?: string
  link?: string
  editor?: boolean | false
  postId?: string
  slug?: string
  lang?: 'en' | 'es' | 'vi' | 'my' | 'he'
}

function Publisher ({ username, publishDate = new Date().toISOString(), link = '#', editor = false, postId = '', slug, lang }: PublisherProps) {
  const { rootData } = useContext(RootContext)
  const { user } = rootData
  const t = useTranslations('PostMenu')
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null)
  const [message, toggleMessage] = useState(false)
  const shareOpen = Boolean(shareAnchorEl)
  const [selectedTheme, setSelectedTheme] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [openDialog, setOpenDialog] = useState<string | undefined>(undefined)
  const [startReadTime, setStartReadTime] = useState(new Date())
  const router = useRouter()
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isPolling, setIsPolling] = useState(true)

  const INACTIVITY_LIMIT = 30 * 60 * 1000 // 30 minutes

  let allowEditor = editor || false
  if (user?.username === username) {
    allowEditor = true
  }

  // format as Mmm, Dd, YYYY
  const date = DateTime.fromISO(publishDate)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShareAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setShareAnchorEl(null)
  }

  function openDefaultEmailClient (url: string) {
    // open default email client with link
    const body = encodeURIComponent(url) // URL encode the body text, use \n for new lines

    // Construct the mailto link
    const mailtoLink = `mailto:?body=${body}`

    // Open the default email client
    window.location.href = mailtoLink
  }

  function shareOnTwitter(url: string, text: string) {
    // Encode the URL and text
    const encodedUrl = encodeURIComponent(url)
    const encodedText = encodeURIComponent(text)

    // Construct Twitter share URL with Web Intent
    const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`

    // Open a new window for the Twitter share dialog
    window.open(twitterShareUrl, 'twitter-share-dialog', 'width=800,height=600')
  }

  function shareOnFacebook(url: string) {
    // Encode the URL
    const encodedUrl = encodeURIComponent(url)

    // Facebook share URL
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`

    // Open a new window for the Facebook share dialog
    window.open(facebookShareUrl, 'facebook-share-dialog', 'width=800,height=600')
  }

  async function handleDelete () {
    setIsDeleting(true)
    if (!openDialog) return null
    if (!postId) return null

    const res = await PATHWAY.delete(`/posts/${postId}`)
    if (res.status === 204) {
      setOpenDialog(undefined)
    }
    setIsDeleting(false)

    // redirect to /feed
    router.push('/feed')
  }

  async function heartbeat () {
    try {
      if (!postId) return null

      const ms = new Date().getTime() - startReadTime.getTime()
      await PATHWAY.post(`/posts/${postId}/heartbeat`, { ms })
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.warn(err)
      }
    }
  }

  async function startPolling () {
    if (!intervalIdRef) return null

    if (!intervalIdRef.current) {
      intervalIdRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          heartbeat()
        }
      }, 30000) // 30 seconds
    }
  }

  // Function to stop polling
  function stopPolling () {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
    setIsPolling(false)
  }

  // Function to reset inactivity timeout
  function resetInactivityTimeout () {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current)
    }

    inactivityTimeoutRef.current = setTimeout(() => {
      stopPolling()
      console.log('Polling stopped due to inactivity')
    }, INACTIVITY_LIMIT)
  }

  useEffect(() => {
    // async function handleUnload (e: BeforeUnloadEvent) {
    //   try {
    //     if (!postId) return null

    //     const ms = new Date().getTime() - startReadTime.getTime()
    //     await PATHWAY.post(`/posts/${postId}/completed`, { ms })
    //     e.preventDefault()
    //     e.returnValue = ''
    //   } catch (err: unknown) {
    //     if (err instanceof Error) {
    //       console.warn(err)
    //     }
    //   }
    // }

    const storedTheme = cookieService.getCookie('theme')
    setSelectedTheme(storedTheme || 'dark')
    setStartReadTime(new Date())
    // window.addEventListener('beforeunload', handleUnload)

    // Start polling when the component mounts and the tab is active
    if (document.visibilityState === 'visible') {
      heartbeat() // Send an initial request when the tab is active
      startPolling()
    }

    // Add event listener to detect when tab visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isPolling) {
        startPolling()
      } else {
        stopPolling()
      }
    }

    // Add event listener to reset inactivity timer on user activity
    const handleUserActivity = () => {
      resetInactivityTimeout()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('mousemove', handleUserActivity)
    document.addEventListener('keydown', handleUserActivity)

    return () => {
      // window.removeEventListener('beforeunload', handleUnload)
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('mousemove', handleUserActivity)
      document.removeEventListener('keydown', handleUserActivity)
      const ms = new Date().getTime() - startReadTime.getTime()
      navigator.sendBeacon(`/api/v1/posts/${postId}/heartbeat?done=yes`, JSON.stringify({ ms }))
    }
  }, [isPolling])

  function localization () {
    const localizedStrings = {
      en: (
        <>
          <span>By</span> <a href={link}>@{username || ''}</a> <span>Published on {date.toFormat('LLL dd, yyyy')}</span>
        </>
      ),
      es: (
        <>
          <span>Por</span> <a href={link}>@{username || ''}</a> <span>Publicado el {date.toFormat('dd LLLL yyyy')}</span>
        </>
      ),
      vi: (
        <>
          <span>Bởi</span> <a href={link}>@{username || ''}</a> <span>Được xuất bản vào ngày {date.toFormat('dd LLLL yyyy')}</span>
        </>
      ),
      my: (
        <>
          <span>@{username || ''}</span> <a href={link}></a> <span>မှ ရေးသားခဲ့သည် {date.toFormat('dd LLLL yyyy')}</span>
        </>
      ),
      he: (
        <>
          <span>מאת</span> <a href={link}>@{username || ''}</a> <span>פורסם בתאריך {date.toFormat('dd LLL, yyyy')}</span>
        </>
      )
    }

    return (
      <div>
        {localizedStrings[lang || 'en']}
      </div>
    )
  }

  return (
    <ThemeClient theme={selectedTheme}>
      <div className={styles.wrapper}>
        <div className={styles.primary}>
          {/* <span className={styles.text}>By</span> <Link href={link} className={styles.link}>{'@'}{(username || '#Publisher')}</Link> <span className={styles.text}>Published on {date.toFormat('LLL dd, yyyy')}</span> */}
          <div>
            {localization()}
          </div>
          {user?.weeklyPublisher &&
            <div className={styles.weeklyPublisher}>
              <Tooltip
                title={t('weekly-publisher-note')}
                aria-label='weekly-publisher'
                id="weekly-publisher"
              >
                <Chip
                  avatar={<WorkspacePremiumIcon />}
                  label={t('weekly-publisher')}
                  variant="outlined"
                />
              </Tooltip>
            </div>
          }
        </div>
        <div className={styles.rightSectionOuter}>
          <div className={styles.rightSection}>
            <Tooltip
              title={t('share')}
              aria-label='share-button'
              id="basic-button"
            >
              <IconButton
                onClick={handleClick}
                size='medium'
              >
                <MoreHorizIcon className={styles.shareIcon} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
      <Menu
        id="basic-menu"
        anchorEl={shareAnchorEl}
        open={shareOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        {allowEditor &&
          <Link href={`/drafts/${postId}`}>
            <MenuItem onClick={handleClose}>
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {t('edit')}
            </MenuItem>
          </Link>
        }
        {allowEditor &&
          <Divider style={{ margin: '0.5rem 0' }} />
        }
        <MenuItem
          dense
          onClick={() => {
            copyLinkService.notifyServerAndCopy(postId, window.location.href)
            toggleMessage(true)
            handleClose()
          }}
        >
          <ListItemIcon>
            <LinkIcon
              className={styles.linkIcon}
              fontSize='large'
            />
          </ListItemIcon>
          {t('copy-link')}
        </MenuItem>
        <MenuItem
          dense
          divider
          style={{ marginBottom: '10px' }}
          onClick={() => {
            openDefaultEmailClient(window.location.href)
            handleClose()
          }}
        >
          <ListItemIcon>
            <SendIcon
              className={styles.linkIcon}
              fontSize='large'
            />
          </ListItemIcon>
          {t('email-link')}
        </MenuItem>

        {slug &&
          <Link href={`/${slug}/translations`}>
            <MenuItem
              dense
              onClick={() => {
                //
                handleClose()
              }}
            >
              <ListItemIcon>
                <TranslateIcon
                  className={styles.linkIcon}
                  fontSize='large'
                />
              </ListItemIcon>
              {t('translate-article')}
            </MenuItem>
          </Link>
        }
        {slug && <Divider style={{ margin: '0.5rem 0' }} />}

        <MenuItem
          dense
          onClick={() => {
            shareOnFacebook(window.location.href)
            handleClose()
          }
          }
          style={{ marginTop: '10px' }}
        >
          <ListItemIcon>
            <FacebookIcon
              className={styles.linkIcon}
              fontSize='large'
            />
          </ListItemIcon>
          {t('share-on-facebook')}
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            shareOnTwitter(window.location.href, 'Check this out!')
            handleClose()
          }}
        >
          <ListItemIcon>
            <XIcon
              className={styles.linkIcon}
              fontSize='large'
            />
          </ListItemIcon>
          {t('share-on-x')}
        </MenuItem>
        {allowEditor &&
          <Divider style={{ margin: '0.5rem 0' }} />
        }
        {allowEditor &&
          <MenuItem
            onClick={() => {
              setOpenDialog('delete')
              handleClose()
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
        }
      </Menu>
      <Snackbar
        open={message}
        autoHideDuration={6000}
        onClose={() => toggleMessage(false)}
        message={t('copied')}
      />
      <DeleteConfirmation
        open={openDialog !== undefined}
        isLoading={isDeleting}
        onCancel={() => {
          setOpenDialog(undefined)
        }}
        onConfirm={async () => {
          await handleDelete()
        }}
      />
    </ThemeClient>
  )
}

export default Publisher
